<template>
  <div class="maps mb-2">
    <div class="d-flex align-items-center mb-1">
      <strong>Vérifier l'adresse : </strong>

      <b-button
        v-if="!showMap"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        class="btn-icon rounded-circle ml-1"
        variant="relief-primary"
        @click="openMapModal"
      >
        <feather-icon icon="MapPinIcon" />
      </b-button>
      <div>
        <b-button
          v-if="showMap"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          class="btn-icon rounded-circle ml-1"
          variant="relief-success"
          @click="closeMapModal"
        >
          <feather-icon icon="CheckIcon" />
        </b-button>
      </div>
      <span v-if="showMap" class="ml-1">{{ currentAddress }}</span>
    </div>
    <l-map v-if="showMap" :center="center" :zoom="zoom">
      <l-tile-layer :url="url"></l-tile-layer>
      <l-marker :lat-lng="markerLatLng">
        <l-popup>Votre projet est ici!</l-popup>
      </l-marker>
    </l-map>
  </div>
</template>

<script>
import { LMap, LMarker, LPopup, LTileLayer } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import { Icon } from "leaflet";
import axios from "axios";
import { BButton } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import {
  convertDMSToLatLong,
  convertUTMToLatLong,
} from "@/utils/convertGeoPositionToLatLong";

delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});
export default {
  name: "SingleMap",
  props: ["type", "address", "latitude", "longitude", "utm_zone", "utm_easting", "utm_northing"],
  directives: {
    Ripple,
  },
  components: {
    BButton,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
  },
  data() {
    return {
      showMap: false,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      zoom: 6,
      center: null,
      markerLatLng: null,
      currentAddress: null,
    };
  },
  watch: {
    markerLatLng: {
      handler(position) {
        if (position) {
          const [lat, lng] = position;

          // Appelle l'API de géocodage pour trouver une adresse
          this.reverseGeocode(lat, lng);
        }
      },
    },
  },
  methods: {
    validateDecimalDegreesCoordinates(lat, lng) {
      // Checks if the coordinates are valid decimal degrees
      const isValidLat = typeof lat === "number" && lat >= -90 && lat <= 90;
      const isValidLng = typeof lng === "number" && lng >= -180 && lng <= 180;

      if (isValidLat && isValidLng) {
        return [lat, lng];
      }
      this.showToast(
        "Coordonnées en Dégrée Décimal invalides",
        "AlertTriangleIcon",
        "danger"
      );

      return null;
    },
    validateDegreesMinutesSecondsCoordinates(lat, lng) {
      // Check if the value conforms to the format of degrees, minutes, and seconds

      const coords = convertDMSToLatLong(lat, lng);
      if (coords) {
        return [coords.latitude, coords.longitude];
      }
      this.showToast(
        "Erreur de conversion des coordonnées Degrée Minute Seconde",
        "AlertTriangleIcon",
        "danger"
      );
      return [null, null];
    },
    validateUTMCoordinates(easting, northing, zone) {
      // Check if value conforms to the format of UTM
      //  Zone should be between 1 and 60
      // const isValidZone = typeof zone === 'number' && zone >= 1 && zone <= 60;

      // Easting should be a positive number
      const isValidEasting =
        typeof easting === "number" && easting >= 100000 && easting <= 999999;

      // Northing should be a positive number (in the northern hemisphere)
      const isValidNorthing =
        typeof northing === "number" && northing >= 0 && northing <= 10000000;

      if (isValidEasting && isValidNorthing) {
        const coords = convertUTMToLatLong(
          easting,
          northing,
          zone.replace("N", "")
        );

        if (coords) {
          return [coords.latitude, coords.longitude];
        }
        this.showToast(
          "Erreur de conversion des coordonnées UTM",
          "AlertTriangleIcon",
          "danger"
        );
        return null;
      }
      this.showToast(
        "Coordonnées en UTM invalides",
        "AlertTriangleIcon",
        "danger"
      );
      return null;
    },
    openMapModal() {
      switch (this.type) {
        case "dd":
          this.markerLatLng = this.validateDecimalDegreesCoordinates(
            this.latitude,
            this.longitude
          );
          break;
        case "dms":
          this.markerLatLng = this.validateDegreesMinutesSecondsCoordinates(
            this.latitude,
            this.longitude
          );
          break;
        case "utm":
          this.markerLatLng = this.validateUTMCoordinates(
            this.utm_easting,
            this.utm_northing,
            this.utm_zone
          );
          break;
        default:
          this.showToast(
            "Une erreur est survenue lors de l'ouverture de la visualisation.",
            "AlertTriangleIcon",
            "warning"
          );
          this.markerLatLng = null;
          break;
      }
      
      if (this.markerLatLng) {
        this.center = this.markerLatLng;
        this.showMap = true;
      }
    },
    closeMapModal() {
      if (this.currentAddress)
        this.$emit("update:address", this.currentAddress);
      this.showMap = false;
      this.center = null;
      // this.markerLatLng = null;
      this.currentAddress = null;
    },
    async onMapClick(position) {
      const { lat, lng } = position;

      if (this.validateDecimalDegreesCoordinates(lat, lng)) {
        this.markerLatLng = [lat, lng];
      } else {
        this.showToast(
          "Coordonnées invalides. Veuillez utiliser des coordonnées en degrés décimaux.",
          "CoffeeIcon",
          "error"
        );
      }
    },
    async reverseGeocode(lat, lng) {
      try {
        // En utilisant l'API Nominatim (OpenStreetMap)
        const response = await axios.get(
          "https://nominatim.openstreetmap.org/reverse",
          {
            params: {
              lat,
              lon: lng,
              format: "json",
            },
          }
        );

        this.currentAddress = response.data.display_name || null;
      } catch (error) {
        console.error("Erreur lors du géodecodage :", error);
      }
    },
    showToast(message, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: icon,
          variant: variant,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.vue2leaflet-map {
  &.leaflet-container {
    height: 350px;
  }
}
</style>
